import { FC } from "react";

import { LinkWithIcon } from "@/atoms/link-with-icon/LinkWithIcon";
import { cn } from "@/lib/utils";
import { useUser } from "modules/user/useUser/useUser";

import { replacePersonaPlaceholders } from "../adonis.helpers";
import { useAdonisChat } from "../AdonisChatProvider/AdonisChatProvider";
import { AdvisorAskInput } from "../AdvisorAskInput/AdvisorAskInput";
import { AdvisorAvatar } from "../AdvisorAvatar/AdvisorAvatar";

interface AdvisorCTAText {
  header: string;
  subheader: string;
  link_text: string;
  chat_box: string;
  chat_submit: string;
}

export interface AdvisorCTAProps {
  ctaText?: AdvisorCTAText;
  className?: string;
  showLink?: boolean;
  onLinkClick?: () => void;
  onChatClick: () => void;
}

export const AdvisorCTA: FC<AdvisorCTAProps> = ({
  ctaText,
  className,
  onChatClick,
  showLink = true,
  onLinkClick,
}) => {
  const { persona } = useAdonisChat();
  const { user } = useUser();

  const username = user?.firstName ?? "";

  const defaultText: AdvisorCTAText = {
    header: "Hey {username}, want to discover your next favorite treatment?",
    subheader: "",
    link_text: "View your personalized recommendations",
    chat_box: "Start typing to chat",
    chat_submit: "Ask {Name}",
  };

  const mergedText: AdvisorCTAText = {
    header: ctaText?.header ?? defaultText.header,
    subheader: ctaText?.subheader ?? defaultText.subheader,
    link_text: ctaText?.link_text ?? defaultText.link_text,
    chat_box: ctaText?.chat_box ?? defaultText.chat_box,
    chat_submit: ctaText?.chat_submit ?? defaultText.chat_submit,
  };

  const text: AdvisorCTAText = {
    header: replacePersonaPlaceholders(
      mergedText.header.replace("{username}", username),
      persona
    ),
    subheader: replacePersonaPlaceholders(
      mergedText.subheader.replace("{username}", username),
      persona
    ),
    link_text: replacePersonaPlaceholders(mergedText.link_text, persona),
    chat_box: replacePersonaPlaceholders(mergedText.chat_box, persona),
    chat_submit: replacePersonaPlaceholders(mergedText.chat_submit, persona),
  };

  return (
    <div
      className={cn("w-full overflow-hidden px-4 py-10", className)}
      style={{
        background:
          "radial-gradient(ellipse at 10% 25%, rgba(255, 255, 255, 0.9) 0%, rgba(238, 218, 197, 0.8) 70%, rgba(222, 193, 156, 0.6) 100%)",
      }}
    >
      <div className="flex flex-col items-center gap-6">
        <AdvisorAvatar advisor={persona.name} size="large" />

        <p className="max-w-[300px] text-center font-serif text-h3 italic text-primary">
          {text.header}
        </p>

        {text.subheader && (
          <p className="text-center font-sans text-body2">{text.subheader}</p>
        )}

        <AdvisorAskInput
          placeholder={text.chat_box}
          buttonText={text.chat_submit}
          onClick={onChatClick}
        />

        {showLink && onLinkClick && (
          <LinkWithIcon text={text.link_text} onClick={onLinkClick} />
        )}
      </div>
    </div>
  );
};
